import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, filter, finalize, switchMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { WarehouseScanService } from 'src/app/core/services/inbound/warehouse-scan/warehouse-scan.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  FileParseType,
  FileReturn,
} from '../../file-upload/file-upload.constants';
import { Intercept } from '../../intercept';
import {
  IMAGE_SRC,
  NO_RESULTS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import { commaSeperateTrackingNumber } from '../../validators';
import {
  ColumnIds,
  ColumnText,
  CONST,
  SORT_OPTION_LIST,
  WarehouseScan,
} from './warehouse-scan-table.constant';

@Component({
  selector: 'app-warehouse-scan-table',
  templateUrl: './warehouse-scan-table.component.html',
  styleUrls: ['./warehouse-scan-table.component.scss'],
  standalone: false,
})
export class WarehouseScanTableComponent extends Intercept implements OnInit {
  readonly _warehouseScanService = inject(WarehouseScanService);

  private readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  private locationService = inject(LocationService);

  readonly currentLocation = signal<CurrentLocation | undefined>(undefined);
  readonly currentUser = signal<UserAdmin | undefined>(undefined);

  readonly CONST = { ...CONST, ...VALIDATION_MESSAGE };
  readonly SORT_OPTION_LIST = SORT_OPTION_LIST;
  readonly SpecialCharacters = SpecialCharacters;
  readonly SRC = IMAGE_SRC;
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly PARSE_TYPE = FileParseType;

  isFileUpload: boolean = false;
  fileResults: any;

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  trackingNumberData = this.createTableColumn(ColumnIds.TRACKING_NUMBER);
  sortDate = this.createTableColumn(ColumnIds.SORT_DATE);
  sortCode = this.createTableColumn(ColumnIds.SORT_CODE);
  location = this.createTableColumn(ColumnIds.LOCATION);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  sortCodeOption: Items[] = [];
  dataSource = new MatTableDataSource<WarehouseScan>();
  form!: FormGroup;
  dropdownOpen = [false];

  constructor(
    public readonly commonUtils: CommonUtils,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  /**
   * OnInit Lifecycle hook
   *
   * @memberof WarehouseScanTableComponent
   */
  ngOnInit(): void {
    this.initializeForm();
    this.dataSource.filterPredicate = this.createFilter();
    this.search();
    this.getCurrentUser();
    this.getCurrentLocation();
  }

  /**
   * get current user
   *
   * @memberof HandlingScanTableComponent
   */
  getCurrentUser() {
    this.userService
      .getCurrentUser$()
      .pipe(
        filter((user) => !!user),
        takeUntilDestroyed(this.destroy),
        switchMap((_user) => {
          this.currentUser.set(_user);
          return this.getIntercepts();
        }),
      )
      .subscribe();
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        this.currentLocation.set(val);
        this.resetState();
      });
  }

  resetState() {
    this.dataSource.data = [];
    this.sortCodeOption = [];
    this.form.reset({
      sortDate: new Date(),
    });
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: any, filter: string) => boolean}
   * @memberof WarehouseScanTableComponent
   */
  private createFilter(): (data: WarehouseScan, filter: string) => boolean {
    return (data: WarehouseScan, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof WarehouseScanTableComponent
   */
  private filterByColumn(
    filterKey: string,
    data: WarehouseScan,
    filters: WarehouseScan,
  ) {
    switch (filterKey) {
      case ColumnIds.SORT_DATE:
        if (filters.sortDate !== '') {
          const filteredStartDate = new Date(
            formatDate(filters.sortDate, 'yyyy-MM-dd', 'en-US'),
          );
          const dataDate = new Date(
            formatDate(data.sortDate, 'yyyy-MM-dd', 'en-US'),
          );
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      case ColumnIds.SORT_CODE: {
        const value = filters[filterKey];
        const rowValue = data[filterKey];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        if (searchValue) {
          return searchValue.includes(searchData);
        }
        return searchData.includes(searchValue);
      }
      default: {
        const value = filters[filterKey as keyof WarehouseScan];
        const rowValue = data[filterKey as keyof WarehouseScan];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        return searchData.includes(searchValue);
      }
    }
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof WarehouseScanTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    if (field.itemId === ColumnIds.SORT_CODE) {
      this.sortCode.formControl.setValue($event);
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof WarehouseScanTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [this.trackingNumberData, this.sortDate, this.location],
      this.dataSource,
      [this.sortCode],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof WarehouseScanTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.dataSource.data, [
      this.trackingNumberData.formControl,
      this.sortDate.formControl,
      this.sortCode.formControl,
      this.location.formControl,
    ]);
    this.resetSelectDropdowns.next(true);
  }

  private initializeForm() {
    this.form = this.fb.group({
      sortDate: [new Date(), Validators.required],
      sortCode: ['', Validators.required],
      trackingNumbers: ['', [Validators.required, commaSeperateTrackingNumber]],
      location: [null],
    });
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof WarehouseScanTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumberData.formControl.value !== '' ||
      this.sortDate.formControl.value !== '' ||
      this.sortCode.formControl.value !== '' ||
      this.location.formControl.value !== ''
    );
  }

  submit(fileData: FileReturn, isFileUpload: boolean) {
    this.isFileUpload = isFileUpload;

    const trackingNumber = fileData.returnData
      .flatMap((val) => val)
      .map((val) => val.trackingNumber);

    const intercept = this.intercepts().filter((item) =>
      trackingNumber.includes(item.trackingNumber),
    );

    if (intercept.length > 0) {
      this.commonUtils.updateInterceptError(
        intercept
          .map((val) => val.trackingNumber)
          .slice(0, 4)
          .toString(),
      );
    } else {
      this.upload(fileData);
    }
  }

  private upload(fileData: FileReturn) {
    if (this.isFileUpload) {
      this.fileUpload(fileData);
    } else {
      this.manualUpload(fileData);
    }
  }

  private manualUpload(_fileData: FileReturn) {
    this.commonUtils.updateLoadingModal(true);

    const request = {
      warehousescans: {
        sortCode: this.form.get('sortCode')?.value,
        sortDate: formatDate(
          this.form.get('sortDate')?.value,
          'yyyy-MM-dd',
          'en-US',
        ),
        locations: [this.form.get('location')?.value],
        trackingNumbers: [this.form.get('trackingNumbers')?.value],
        fromFileUpload: this.isFileUpload,
      },
    };

    this._warehouseScanService
      .addWarehouseScan(request)
      .pipe(
        take(1),
        finalize(() => this.commonUtils.updateLoadingModal(false)),
      )
      .subscribe((val) => {
        if (!!val.response.whseScans) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);

          const tableData = this.dataSource.data;
          this.dataSource.data = [
            ...((val?.response?.whseScans as WarehouseScan[]) ?? []),
            ...tableData,
          ];

          this.initializeForm();
        }
      });
  }

  private fileUpload(fileData: FileReturn) {
    this.commonUtils.updateLoadingModal(true);

    const apis$ = fileData.returnData.map((record) => {
      const request = {
        warehousescans: {
          sortCode: this.form.get('sortCode')?.value,
          sortDate: formatDate(
            this.form.get('sortDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
          locations: record.map((val) => val.location ?? ''),
          trackingNumbers: record.map((val) => val.trackingNumber),
          fromFileUpload: this.isFileUpload,
        },
      };

      return {
        numbers: record.map((val) => val.trackingNumber),
        call$: this._warehouseScanService.addWarehouseScan(request),
      };
    });

    this.commonUtils
      .runFileUploadCallsInChunks(
        apis$,
        this.commonUtils.generateUploadString(
          new Date(),
          this.currentLocation()?.location?.locationDetails.code || '',
          this.currentUser()?.loginId || '',
        ),
        fileData.fileName,
        fileData.fileSize,
      )
      .subscribe((succeeded) => {
        if (succeeded) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
      });
  }
}
